import axios from 'axios';

const productionApiUrl = 'https://protova.onrender.com';
const configuredApiUrl = process.env.REACT_APP_API_URL;
const resolvedApiUrl = process.env.NODE_ENV === 'production'
  ? (configuredApiUrl || productionApiUrl)
  : (configuredApiUrl || '');

const api = axios.create({
  baseURL: resolvedApiUrl,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const searchPapers = (query, sort = 'relevance', yearFrom = null, yearTo = null) =>
  api.post('/api/papers/search', { query, sort, year_from: yearFrom, year_to: yearTo });

export const analyzeSimilarity = (papers) =>
  api.post('/api/similarity/analyze', { papers });

export const generateProtocol = (papers) =>
  api.post('/api/protocol/generate', { papers });

export const suggestDesign = (subject, experimental_design, papers) =>
  api.post('/api/protocol/suggest-design', { subject, experimental_design, papers });

export const generateFlowchart = (methods_text) =>
  api.post('/api/protocol/generate-flowchart', { methods_text });

export const summarizeSearch = (query, papers) =>
  api.post('/api/insights/summarize', { query, papers });

export const findClinicalTrials = (query) =>
  api.get('/api/insights/clinical-trials', { params: { query } });

export const exportCSV = (papers) =>
  api.post('/api/insights/export-csv', { papers }, { responseType: 'blob' });

// Workspaces
export const listWorkspaces = () => api.get('/api/workspaces/');
export const saveWorkspace = (name, papers) => api.post('/api/workspaces/', { name, papers });
export const updateWorkspace = (id, name, papers) => api.put(`/api/workspaces/${id}`, { name, papers });
export const deleteWorkspace = (id) => api.delete(`/api/workspaces/${id}`);

// PDF export
export const exportProtocolPDF = (title, sections) =>
  api.post('/api/export/protocol-pdf', { title, sections }, { responseType: 'blob' });

// Protocol versioning
export const saveProtocolVersion = (name, title, sections) =>
  api.post('/api/protocol-versions/', { name, title, sections });

export const listProtocolVersions = () =>
  api.get('/api/protocol-versions/');

export const getProtocolVersion = (id) =>
  api.get(`/api/protocol-versions/${id}`);

export const deleteProtocolVersion = (id) =>
  api.delete(`/api/protocol-versions/${id}`);

// Protocol sharing
export const publishProtocolVersion = (id) =>
  api.post(`/api/protocol-versions/${id}/publish`);

export const unpublishProtocolVersion = (id) =>
  api.post(`/api/protocol-versions/${id}/unpublish`);

export const getPublicProtocol = (slug) =>
  axios.get(`${resolvedApiUrl}/api/public/protocols/${slug}`);

// Bulk import
export const importPapers = (dois, bibtex, ris) =>
  api.post('/api/papers/import', { dois, bibtex, ris });

export const importPapersPDF = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/api/papers/import-pdf', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Hypothesis generator
export const generateHypotheses = (subject, papers) =>
  api.post('/api/hypothesis/generate', { subject, papers });

// Methods extractor
export const extractMethods = (papers) =>
  api.post('/api/methods/extract', { papers });

// Grant assistant
export const reframeForGrant = (protocol, grant_type) =>
  api.post('/api/grant/reframe', { protocol, grant_type });

// Chat with PDF
export const uploadPDFForChat = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/api/chat/pdf/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const askPDFQuestion = (session_id, question) =>
  api.post('/api/chat/pdf/ask', { session_id, question });
export const askPaperQuestion = (paper, question, history = []) =>
  api.post('/api/chat/pdf/ask-paper', { paper, question, history });

// Zotero
export const connectZotero = (api_key, user_id) =>
  api.post('/api/zotero/connect', { api_key, user_id });
export const disconnectZotero = () => api.delete('/api/zotero/connect');
export const getZoteroCollections = () => api.get('/api/zotero/collections');
export const getZoteroItems = (collection_id = null, limit = 50) =>
  api.get('/api/zotero/items', { params: { collection_id, limit } });

// Literature review
export const generateLiteratureReview = (topic, papers) =>
  api.post('/api/literature-review/generate', { topic, papers });

// Reference formatter
export const formatReferences = (papers, style) =>
  api.post('/api/references/format', {
    papers,
    style,
  });

// Knowledge graph
export const analyzeConceptGraph = (paper) =>
  api.post('/api/graph/analyze', paper);

export const getWorkspaceGraph = (papers) =>
  api.post('/api/graph/workspace', { papers });

// Paper library
export const listPaperLibrary = () => api.get('/api/papers/library');
export const deletePaperFromLibrary = (id) => api.delete(`/api/papers/library/${id}`);

export default api;
